export const colorTokens = {
  neutral: {
    100: '#f2f2f2',
    200: '#e9e9e9',
    300: '#d6d6d6',
    400: '#bbbbbb',
    500: '#9a9a9a',
    600: '#838383',
    700: '#676767',
    800: '#444444',
    900: '#1a1a1a',
    white: '#ffffff',
    black: '#000000',
  },
  gray: {
    50: '#f2f3f6',
    100: '#edf2f8',
    200: '#d9e2ed',
    300: '#cdd5e2',
    400: '#c2cbd5',
    500: '#b2b9c1',
    600: '#a3abb5',
    700: '#808c94',
    800: '#67727a',
    900: '#1f1f1f',
  },
  brand: {
    50: '#f2f6fc',
    100: '#f1f7ff',
    200: '#e8f2ff',
    300: '#dbeaff',
    400: '#bad8ff',
    500: '#91c0ff',
    600: '#68a9ff',
    700: '#358cff',
    800: '#1972ea',
    900: '#0054c6',
  },
  red: {
    50: '#fcf2f2',
    100: '#fcefed',
    200: '#ffe5e5',
    300: '#ffc9c9',
    400: '#ffb1b1',
    500: '#ff8484',
    600: '#ff5353',
    700: '#ff2732',
    800: '#d2000b',
    900: '#91030b',
  },
  yellow: {
    50: '#fefce8',
    100: '#fcfce9',
    200: '#fffbcc',
    300: '#feefa3',
    400: '#ffec59',
    500: '#e8b42f',
    600: '#d1981d',
    700: '#b87f13',
    800: '#956a04',
    900: '#644900',
  },
  teal: {
    100: '#e9f9fc',
    200: '#c2f5e9',
    300: '#a2eddb',
    400: '#72ddc3',
    500: '#39cfc9',
    600: '#02aaa4',
    700: '#06a09b',
    800: '#047d95',
    900: '#005566',
  },
  green: {
    50: '#f2fde6',
    100: '#e2fde6',
    200: '#cfffc5',
    300: '#abfa95',
    400: '#70e536',
    500: '#42c801',
    600: '#3ab100',
    700: '#309300',
    800: '#277800',
    900: '#216500',
  },
  orange: {
    50: '#fff7ed',
    100: '#ffedd5',
    200: '#fed7aa',
    300: '#fdba74',
    400: '#fb923c',
    500: '#f97316',
    600: '#ea580c',
    700: '#c2410c',
    800: '#9a3412',
    900: '#7c2d12',
  },
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  purple: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7e22ce',
    800: '#6b21a8',
    900: '#581c87',
  },
  indigo: {
    50: '#eef2ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
    800: '#3730a3',
    900: '#312e81',
  },
} as const;

export const semanticColors = {
  background: {
    primary: '#f8fafc',
    primaryHover: colorTokens.gray[100],
    secondary: colorTokens.neutral.white,
    secondaryHover: colorTokens.neutral[100],
    tertiary: colorTokens.brand[200],
    tertiaryHover: colorTokens.brand[300],
    disabled: colorTokens.neutral[200],
    brand: colorTokens.brand[800],
    brandHover: colorTokens.brand[700],
    success: {
      primary: colorTokens.green[500],
      primaryHover: colorTokens.green[600],
      secondary: colorTokens.green[100],
      secondaryHover: colorTokens.green[200],
    },
    info: {
      primary: colorTokens.teal[500],
      primaryHover: colorTokens.teal[600],
      secondary: colorTokens.teal[100],
      secondaryHover: colorTokens.teal[200],
    },
    warning: {
      primary: colorTokens.yellow[500],
      primaryHover: colorTokens.yellow[600],
      secondary: colorTokens.yellow[200],
      secondaryHover: colorTokens.yellow[300],
    },
    danger: {
      primary: colorTokens.red[700],
      primaryHover: colorTokens.red[800],
      secondary: colorTokens.red[200],
      secondaryHover: colorTokens.red[400],
    },
  },
  text: {
    white: colorTokens.neutral.white,
    primary: colorTokens.neutral.black,
    secondary: colorTokens.gray[900],
    tertiary: colorTokens.gray[800],
    disabled: colorTokens.neutral[400],
    brand: colorTokens.brand[900],
    onDanger: {
      primary: colorTokens.red[100],
      secondary: colorTokens.red[800],
      tertiary: colorTokens.red[900],
    },
    onWarning: {
      primary: colorTokens.yellow[900],
      secondary: colorTokens.yellow[100],
    },
    onSuccess: {
      primary: colorTokens.green[900],
      secondary: colorTokens.green[100],
    },
    onInfo: {
      primary: colorTokens.teal[800],
      secondary: colorTokens.teal[100],
    },
  },
  icon: {
    primary: colorTokens.neutral.black,
    secondary: colorTokens.gray[900],
    tertiary: colorTokens.gray[800],
    disabled: colorTokens.neutral[400],
    brand: colorTokens.brand[900],
    onBrand: colorTokens.brand[100],
    danger: colorTokens.red[800],
    onInfo: colorTokens.teal[500],
    onSuccess: colorTokens.green[900],
    onWarning: colorTokens.yellow[500],
    onDanger: colorTokens.red[500],
    white: colorTokens.neutral.white,
  },
  border: {
    primary: colorTokens.neutral[300],
    secondary: colorTokens.neutral[100],
    tertiary: colorTokens.gray[300],
    disabled: colorTokens.neutral[400],
    brand: colorTokens.brand[800],
    warning: colorTokens.yellow[300],
    info: colorTokens.teal[200],
    success: colorTokens.green[300],
    dangerPrimary: colorTokens.red[800],
    dangerSecondary: colorTokens.red[400],
  },
} as const; 